import { isNullish } from '@caresend/utils';
import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { isMobile } from 'mobile-device-detect';
import { defineComponent, computed, ref, onMounted, watchEffect, watch, nextTick } from 'vue';
import { SHORT_MONTHS_OPTIONS, MONTH_OPTIONS, properMod, SHORT_MONTH_MAP, MONTH_MAP, dateRegexWithSpaces, dateRegex, slashCount, getTextInputHelpers } from './helpers.js';
import { DatePickerType } from './model.js';
import __vue_component__ from '../../ui/Icon.vue.js';
import { delayedComputed } from '../../../composables/dom.js';
import '@caresend/types';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../../router/helpers/initRouter.js';
import '../../specific/portal/store/portal/sessionStore.js';
import 'file-saver';
import '../../../database/firebaseConfig/cloudFunctions.js';
import '../../../database/encryption/model.js';
import 'js-sha256';
import 'lodash.clonedeep';
import 'lodash.isequal';
import '../../specific/portal/store/storeUtilities/dbSync/dbSyncStore.js';
import '../../../store/modules/app/model.js';
import 'firebase/database';
import 'firebase/auth';
import '../../../database/firebaseConfig/init.js';
import '../../../database/firebaseConfig/methods.js';
import '../../../functions/configuration/icons.js';
import '../../../database/algoliaConfig/indices.js';
import '../../../functions/tracking/index.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'immutability-helper';
import '../../../router/routes/dev/model.js';
import '../../../router/routes/dev/routes.js';
import '../../../router/routes/dashboard/model.js';
import '../../../router/routes/pro/model.js';
import '../../../router/routes/visitsPage/model.js';
import '../../../router/routes/orderFlow/model.js';
import '../../../router/routes/orderFlow/privateHelpers.js';
import '../../../router/routes/orderFlow/routes.js';
import '../../../router/routes/schedulingFlow/model.js';
import '../../../router/routes/schedulingFlow/routes.js';
import '../../../router/routes/chartingFlow/model.js';
import '../../../router/routes/chartingFlow/routes.js';
import '../../../router/routes/portal/portalRoutes.js';
import '../../../router/routes/portal/model.js';
import '../../../router/routes/visitsPage/routes.js';
import { isExactTheme } from '../../../store/modules/app/helpers.js';
import '../../../store/modules/auth/index.js';
import '../../../store/pinia/draftWaypoints/index.js';
import '../../../store/modules/chartingFlow/chartingFlowSteps/steps.js';
import '../../../store/modules/chartingFlow/model.js';
import '../../../store/modules/office/index.js';
import '../../specific/VisitsPage/subComponents/OrdersVisitsFilters/model.js';
import '../../../store/modules/office/model.js';
import '../../../store/modules/orderFlow/index.js';
import '../../../store/modules/procedures/model.js';
import '../../../store/modules/schedulingFlow/index.js';
import '../../../store/modules/schedulingFlow/model.js';
import '../../specific/dev/AdvancedSettings/subViews/FeatureFlags/model.js';

dayjs.extend(utc);
dayjs.extend(tz);
var script = defineComponent({
    name: 'DatePicker',
    components: { Icon: __vue_component__ },
    props: {
        disabled: Boolean,
        type: {
            type: String,
            default: DatePickerType.DEFAULT,
        },
        highlight: Boolean,
        icon: {
            type: String,
            default: 'calendar-day',
        },
        unselectableDates: {
            type: Function,
            default: () => false,
        },
        initToToday: Boolean,
        label: {
            type: String,
            default: null,
        },
        placeholder: {
            type: String,
            default: 'Click to select…',
        },
        size: {
            type: String,
            default: 'sm',
        },
        timeZone: {
            type: String,
            default: undefined,
        },
        /**
         * If this prop is set to true, regardless of the end user's time zone, the
         * emitted value will be 00:00:00 on the selected date in UTC.
         */
        useStartOfUtcDate: Boolean,
        value: {
            type: Number,
            default: null,
        },
        position: {
            type: String,
            default: 'is-bottom-right',
        },
    },
    emits: [
        'input',
        'customTextInputFocused',
        'customCalendarOpened',
        'userInteractedWithCalendar',
    ],
    setup(props, { emit }) {
        var _a, _b, _c;
        const showCustomDateInput = computed(() => props.type !== DatePickerType.DEFAULT);
        const isExact = computed(() => isExactTheme());
        /**
         * When type !== 'default', the focused date helps us interact with which dates will be currently
         * visible in the calendar through the custom header. For example, setting the month or year of
         * the focusedDate will change the calendar's view to the days in that month and year.
         *
         * This is separate from the actual date value since that indicates what date has been selected by the user.
         *
         * defaults to today's date
         */
        const focusedDate = ref(new Date());
        /**
         * custom header only
         * Show 3-character month names if isExact is true, otherwise show the full month name.
         */
        const months = computed(() => isExact.value ? SHORT_MONTHS_OPTIONS : MONTH_OPTIONS);
        /**
         * custom header only
         * The years available in the calendar.
         * defaults to the years [1900, 1901, ..., Current Year + 15]
         */
        const years = ref(Array.from({ length: focusedDate.value.getFullYear() + 15 - 1900 + 1 }, (_, i) => 1900 + i));
        /** custom header only - the currently focused month */
        const focusedMonth = ref((_b = (_a = months.value.filter((item) => { var _a; return item.value === ((_a = focusedDate.value) === null || _a === void 0 ? void 0 : _a.getMonth()); })[0]) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : (isExact.value ? 'Jan' : 'January'));
        /** custom header only - the currently focused year */
        const focusedYear = ref((_c = focusedDate.value) === null || _c === void 0 ? void 0 : _c.getFullYear());
        const setFocusedMonthData = (monthNumber, emitInteraction = false) => {
            if (props.disabled)
                return;
            const baseDate = focusedDate.value;
            const newDate = new Date(baseDate);
            newDate.setMonth(monthNumber);
            focusedDate.value = newDate;
            const monthName = isExact.value ? SHORT_MONTH_MAP[newDate.getMonth()] : MONTH_MAP[newDate.getMonth()];
            focusedMonth.value = monthName !== null && monthName !== void 0 ? monthName : (isExact.value ? 'Jan' : 'January');
            if (focusedYear.value !== newDate.getFullYear())
                focusedYear.value = newDate.getFullYear();
            if (emitInteraction)
                emit('userInteractedWithCalendar', 'month');
        };
        const selectMonth = (option) => {
            var _a;
            if (option === null)
                return;
            setFocusedMonthData((_a = option.value) !== null && _a !== void 0 ? _a : 0);
        };
        const selectYear = (value) => {
            focusedDate.value = new Date(focusedDate.value.setFullYear(value));
            focusedYear.value = value;
        };
        /**
         * Hack to ensure dropdown items have a "selected" look + the currently selected item is centered
         * on the dropdown scroll view.
         */
        const updateDropdownSelected = (event) => nextTick(() => {
            var _a, _b, _c;
            const inputElement = event.target;
            if (!inputElement)
                return;
            // If number, it's a year, otherwise it's a month
            const source = /^\d+$/.test(inputElement.value) ? 'year' : 'month';
            const scrollContent = (_b = (_a = inputElement.parentElement) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.querySelector('.dropdown-menu .dropdown-content');
            const dropdownItems = Array.from((_c = scrollContent === null || scrollContent === void 0 ? void 0 : scrollContent.querySelectorAll('a.dropdown-item')) !== null && _c !== void 0 ? _c : []);
            if (!scrollContent || dropdownItems.length === 0)
                return;
            const selectedIndex = source === 'year'
                ? focusedDate.value.getFullYear() - 1900
                : focusedDate.value.getMonth();
            dropdownItems.forEach((item, index) => {
                if (index === selectedIndex)
                    item.classList.add('DatePicker__is-selected-dropdown-item');
                else
                    item.classList.remove('DatePicker__is-selected-dropdown-item');
            });
            // Manual styling values... might get messy if you mess around with the styles so please update accordingly
            const itemHeight = 36;
            const scrollAreaHeight = 300;
            const padding = 8;
            const itemInView = itemHeight * (selectedIndex) + padding;
            const scrollTargetDistance = Math.max(0, itemInView + (itemHeight / 2) - (scrollAreaHeight / 2));
            scrollContent.scrollTop = scrollTargetDistance;
        });
        const containerIsNarrow = ref(false);
        /**
         * When type === 'dropdown-calendar', this ref is used to track whether the calendar has been
         * opened by the user through the trigger button.
         */
        const calendarIsOpen = ref(false);
        /**
         * When type === 'dropdown-calendar', this computed ref is used to delay the opening of the
         * calendar by 150ms to ensure animations play seamlessly.
         */
        const delayedCalendarisOpen = delayedComputed(() => calendarIsOpen.value, 150);
        const classNames = computed(() => {
            const names = ['DatePicker'];
            switch (props.size) {
                case 'sm':
                    names.push('DatePicker--sm');
                    names.push('DatePicker__datepicker--sm');
                    break;
                case 'md':
                    names.push('DatePicker--md');
                    names.push('DatePicker__datepicker--md');
                    break;
                case 'lg':
                    names.push('DatePicker--lg');
                    names.push('DatePicker__datepicker--lg');
                    break;
            }
            if (!calendarIsOpen.value)
                names.push('DatePicker--popup-mode-closed');
            if (props.disabled)
                names.push('DatePicker--disabled');
            if (showCustomDateInput.value)
                names.push('DatePicker--ensure-inline');
            if (containerIsNarrow.value)
                names.push('DatePicker--container-is-narrow');
            if (props.type === DatePickerType.DROPDOWN_CALENDAR)
                names.push('DatePicker--as-popup');
            if (props.highlight)
                names.push('Datepicker--highlight');
            return names;
        });
        /** Custom format to show for selected dates in the input */
        const dateFormatter = (date) => dayjs(date).format('MM / DD / YYYY');
        const getTimeDifference = (date1, date2) => {
            const adjustmentFormat = 'MM/DD/YY hh:mm a';
            const reformattedDate1 = dayjs(date1.format(adjustmentFormat), adjustmentFormat);
            const reformattedDate2 = dayjs(date2.format(adjustmentFormat), adjustmentFormat);
            return reformattedDate2.diff(reformattedDate1);
        };
        const inputTag = ref(null);
        const date = ref(props.initToToday ? new Date() : null);
        /**
         * If our custom input is used (type !== 'default'), this ref is used to track the last
         * "valid" value that was entered into the input such that we can revert to this value if
         * the user enters a value that would not count as a valid date. This gives the illusion
         * that only valid date formats are accepted, with the exception of number inputs that we
         * manually override and 'quantize' to a valid date.
         */
        const lastValidValue = ref('');
        const updateInputTag = () => {
            if (!showCustomDateInput.value || !inputTag.value)
                return;
            inputTag.value.value = dayjs(date.value).format('MM / DD / YYYY');
            lastValidValue.value = inputTag.value.value.replaceAll(' ', '');
        };
        const onCustomTextInputFocused = () => {
            if (inputTag.value && dateRegexWithSpaces.test(inputTag.value.value)) {
                inputTag.value.value = inputTag.value.value.replaceAll(' ', '');
                lastValidValue.value = inputTag.value.value;
            }
            emit('customTextInputFocused');
        };
        const onCustomTextInputBlurred = () => {
            if (inputTag.value && dateRegex.test(inputTag.value.value)) {
                updateInputTag();
            }
        };
        const onCustomCalendarButtonClicked = () => {
            if (props.disabled)
                return;
            calendarIsOpen.value = !calendarIsOpen.value;
            if (calendarIsOpen.value)
                emit('customCalendarOpened');
        };
        const buefySize = computed(() => {
            switch (props.size) {
                case 'sm':
                    return 'is-small';
                case 'md':
                    return 'is-medium';
                case 'lg':
                    return 'is-large';
            }
        });
        const inputHandler = (dateValue) => {
            var _a;
            if (!dateValue) {
                emit('input', null);
                return;
            }
            const dayjsObj = dayjs(dateValue);
            let timestamp = dayjsObj.startOf('day').valueOf();
            if (props.useStartOfUtcDate) {
                const startOfUtcDate = dayjs.utc()
                    .year(dayjsObj.year())
                    .month(dayjsObj.month())
                    .date(dayjsObj.date())
                    .hour(0)
                    .minute(0)
                    .second(0)
                    .millisecond(0);
                timestamp = dayjs(startOfUtcDate).valueOf();
            }
            if (props.timeZone) {
                const timeZoneAdjusted = dayjsObj.tz(props.timeZone);
                const difference = getTimeDifference(timeZoneAdjusted, dayjsObj);
                const adjustedTime = dayjsObj.tz(props.timeZone).add(difference, 'ms');
                timestamp = adjustedTime.toDate().valueOf();
            }
            (_a = inputTag.value) === null || _a === void 0 ? void 0 : _a.blur();
            emit('input', timestamp);
        };
        const inputHandlerWithDayChangeEmit = (dateValue) => {
            if (dateValue)
                emit('userInteractedWithCalendar', 'day');
            inputHandler(dateValue);
        };
        const doNothing = (event) => {
            event.preventDefault();
            event.stopPropagation();
        };
        const isClearVisible = ref(false);
        const clearInputTag = () => {
            if (inputTag.value) {
                isClearVisible.value = false;
                inputTag.value.value = '';
                lastValidValue.value = '';
                date.value = null;
                emit('input', undefined);
                /**
                 * (non-default case only) Once the date is set to null, the calendar gets in a weird state
                 * so we wait for those changes to propagate and then set the focused date again.
                 * such that the calendar is in a good state.
                 */
                if (showCustomDateInput.value) {
                    nextTick(() => { focusedDate.value = new Date(focusedDate.value); });
                }
            }
        };
        const lockCaretInCustomInput = (event) => {
            var _a, _b, _c, _d;
            const valueLength = (_b = (_a = inputTag.value) === null || _a === void 0 ? void 0 : _a.value.length) !== null && _b !== void 0 ? _b : 0;
            if (valueLength > 0) {
                event.preventDefault();
                event.stopPropagation();
                // Ensure caret is always at the end of the input
                (_c = inputTag.value) === null || _c === void 0 ? void 0 : _c.focus();
                (_d = inputTag.value) === null || _d === void 0 ? void 0 : _d.setSelectionRange(valueLength, valueLength);
            }
        };
        /**
         * Used to make the custom text input work like a normal date picker input
         * this only matterns if type !== 'default'
        */
        const onTextInput = (event) => {
            doNothing(event);
            const htmlInputTarget = event.target;
            htmlInputTarget.value = htmlInputTarget.value.replace('-', '/').replace(' ', '/');
            const slashCountValue = slashCount(htmlInputTarget.value);
            const { handleTextInputInvalidStateHelper, handleNoSlashTextInputHelper, handleSingleSlashTextInputHelper, handleMiscTextInputLogicHelper, handleValidInputDateHelper, } = getTextInputHelpers(inputTag, lastValidValue, isClearVisible, inputHandler);
            // Empty state check
            if (htmlInputTarget.value.length === 0 || isNullish(htmlInputTarget.value)) {
                clearInputTag();
                return;
            }
            // Invalid state check (not numbers with slashes, leading zeros, multiple slashes, etc.)
            if (handleTextInputInvalidStateHelper(htmlInputTarget, slashCountValue))
                return;
            // Update the last valid value if the new value is shorter
            if (lastValidValue.value.length > htmlInputTarget.value.length) {
                lastValidValue.value = htmlInputTarget.value;
                return;
            }
            // Handle no-slash case
            if (slashCountValue === 0 && handleNoSlashTextInputHelper(htmlInputTarget))
                return;
            const parts = htmlInputTarget.value.split('/');
            // Handle single-slash case
            if (slashCountValue === 1 && handleSingleSlashTextInputHelper(htmlInputTarget, parts))
                return;
            // Handle all-slash case
            if (slashCountValue === 2 && parts[1] === '0' && htmlInputTarget.value.at(-1) === '/') {
                htmlInputTarget.value = lastValidValue.value;
                return;
            }
            // Misc bits of logic - hard to categorize into a single concept
            if (handleMiscTextInputLogicHelper(htmlInputTarget, parts))
                return;
            // Finally, do some work if the current state of the input is fully valid
            handleValidInputDateHelper(htmlInputTarget);
        };
        const inputClassNames = computed(() => {
            const names = ['DatePicker__input'];
            if (showCustomDateInput.value)
                names.push('DatePicker__input--ensure-inline');
            return names;
        });
        const container = ref(null);
        /** We've seen weird calendar sizing issues on some mobile cases where the container
         * is really small and the calendar is too big since some of the changes are only using
         * media queries.
         *
         * Using the resize observer allows us to use a class conditionally to adapt the calendar size
         */
        const observeContainer = () => {
            if (!container.value)
                return;
            const observer = new ResizeObserver((entries) => {
                var _a, _b;
                const width = (_b = (_a = entries[0]) === null || _a === void 0 ? void 0 : _a.contentRect.width) !== null && _b !== void 0 ? _b : 0;
                containerIsNarrow.value = width < 375;
            });
            observer.observe(container.value);
        };
        onMounted(() => {
            if (props.initToToday && !props.value) {
                date.value = new Date();
                updateInputTag();
            }
            observeContainer();
        });
        watchEffect(() => {
            if (isNullish(props.value))
                return;
            const dayjsObj = dayjs(props.value);
            if (props.useStartOfUtcDate) {
                const startOfUtcDate = new Date(dayjsObj.utc().year(), dayjsObj.utc().month(), dayjsObj.utc().date());
                date.value = dayjs(startOfUtcDate).toDate();
                updateInputTag();
                return;
            }
            const localTz = dayjs.tz.guess();
            if (props.timeZone && localTz !== props.timeZone) {
                const localTimeZoneDate = new Date(dayjsObj.tz(props.timeZone).year(), dayjsObj.tz(props.timeZone).month(), dayjsObj.tz(props.timeZone).date());
                date.value = dayjs(localTimeZoneDate).toDate();
                updateInputTag();
                return;
            }
            date.value = dayjsObj.startOf('day').toDate();
            updateInputTag();
        });
        watch(date, () => {
            if (date.value === null) {
                isClearVisible.value = false;
            }
            else {
                // Only update if the month/year is different
                if (focusedDate.value.getMonth() !== date.value.getMonth()) {
                    setFocusedMonthData(date.value.getMonth());
                }
                if (focusedDate.value.getFullYear() !== date.value.getFullYear()) {
                    selectYear(date.value.getFullYear());
                }
            }
            if (date.value && !isClearVisible.value)
                isClearVisible.value = true;
            if (calendarIsOpen.value)
                calendarIsOpen.value = false;
        });
        watch(isExact, (value) => {
            var _a;
            const source = value ? SHORT_MONTH_MAP : MONTH_MAP;
            focusedMonth.value = (_a = source[focusedDate.value.getMonth()]) !== null && _a !== void 0 ? _a : (value ? 'Jan' : 'January');
        }, { immediate: true });
        return {
            buefySize,
            calendarIsOpen,
            delayedCalendarisOpen,
            classNames,
            clearInputTag,
            date,
            dateFormatter,
            doNothing,
            focusedDate,
            focusedMonth,
            focusedYear,
            inputClassNames,
            container,
            inputHandlerWithDayChangeEmit,
            inputTag,
            isClearVisible,
            isMobile,
            lockCaretInCustomInput,
            months,
            onTextInput,
            onCustomTextInputFocused,
            onCustomCalendarButtonClicked,
            onCustomTextInputBlurred,
            properMod,
            selectMonth,
            selectYear,
            setFocusedMonthData,
            showCustomDateInput,
            updateDropdownSelected,
            years,
        };
    },
});

export { script as default };
